import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Upload, CheckCircle, X, Building2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterBusiness() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    email: "",
    phone: "",
    category_id: "",
    address: "",
    website: "",
    logo_base64: null,
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category_id: value }));
    if (errors.category_id) {
      setErrors(prev => ({ ...prev, category_id: null }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setFormData(prev => ({ ...prev, logo_base64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_base64: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Le nom est requis";
    if (!formData.description.trim()) newErrors.description = "La description est requise";
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }
    if (!formData.phone.trim()) newErrors.phone = "Le téléphone est requis";
    if (!formData.category_id) newErrors.category_id = "La catégorie est requise";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs");
      return;
    }

    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/businesses/register`, formData);
      setSuccess(true);
      setVerificationToken(res.data.verification_token);
      toast.success("Inscription réussie!");
    } catch (e) {
      const message = e.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-3xl p-8 md:p-12 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-4">
            Inscription réussie !
          </h1>
          <p className="text-gray-600 mb-6">
            Votre entreprise a été enregistrée. Un email de vérification vous a été envoyé.
          </p>
          
          {/* Demo: Direct verification link */}
          {verificationToken && (
            <div className="bg-lagoon-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-lagoon-900 mb-2 font-medium">
                Lien de vérification (démo) :
              </p>
              <Link 
                to={`/verify/${verificationToken}`}
                className="text-coral hover:text-coral-600 text-sm break-all underline"
                data-testid="verification-link"
              >
                Cliquez ici pour vérifier votre compte
              </Link>
            </div>
          )}
          
          <Button onClick={() => navigate('/')} className="btn-primary" data-testid="back-home-btn">
            Retour à l'accueil
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-lagoon-900 mb-4">
                Inscrire mon entreprise
              </h1>
              <p className="text-gray-600">
                Remplissez le formulaire ci-dessous pour ajouter votre entreprise à l'annuaire
              </p>
            </div>

            <form onSubmit={handleSubmit} className="glass-card rounded-3xl p-8 md:p-10" data-testid="register-form">
              {/* Logo Upload */}
              <div className="mb-8">
                <Label className="text-lagoon-900 font-semibold mb-3 block">Logo de l'entreprise</Label>
                <div className="flex items-center gap-6">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-24 h-24 rounded-xl object-cover"
                        data-testid="logo-preview"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-coral rounded-full flex items-center justify-center text-white hover:bg-coral-600"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-lagoon-50 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-lagoon" />
                    </div>
                  )}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                      data-testid="logo-input"
                    />
                    <label 
                      htmlFor="logo-upload"
                      className="btn-primary inline-flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      {logoPreview ? 'Changer' : 'Télécharger'}
                    </label>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG ou GIF. Max 5MB.</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-lagoon-900 font-semibold">
                    Nom de l'entreprise <span className="text-coral">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl ${errors.name ? 'border-coral' : ''}`}
                    placeholder="Ex: Pension Moana"
                    data-testid="name-input"
                  />
                  {errors.name && <p className="text-coral text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Category */}
                <div>
                  <Label className="text-lagoon-900 font-semibold">
                    Catégorie <span className="text-coral">*</span>
                  </Label>
                  <Select value={formData.category_id} onValueChange={handleCategoryChange}>
                    <SelectTrigger 
                      className={`mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl ${errors.category_id ? 'border-coral' : ''}`}
                      data-testid="category-select"
                    >
                      <SelectValue placeholder="Sélectionnez une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category_id && <p className="text-coral text-sm mt-1">{errors.category_id}</p>}
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-lagoon-900 font-semibold">
                    Description <span className="text-coral">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`mt-2 bg-white/50 border-lagoon-100 rounded-xl min-h-[120px] ${errors.description ? 'border-coral' : ''}`}
                    placeholder="Décrivez votre activité..."
                    data-testid="description-input"
                  />
                  {errors.description && <p className="text-coral text-sm mt-1">{errors.description}</p>}
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-lagoon-900 font-semibold">
                      Email <span className="text-coral">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl ${errors.email ? 'border-coral' : ''}`}
                      placeholder="contact@entreprise.pf"
                      data-testid="email-input"
                    />
                    {errors.email && <p className="text-coral text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-lagoon-900 font-semibold">
                      Téléphone <span className="text-coral">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl ${errors.phone ? 'border-coral' : ''}`}
                      placeholder="87 12 34 56"
                      data-testid="phone-input"
                    />
                    {errors.phone && <p className="text-coral text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                {/* Address & Website */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="address" className="text-lagoon-900 font-semibold">
                      Adresse
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                      placeholder="Papeete, Tahiti"
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website" className="text-lagoon-900 font-semibold">
                      Site web
                    </Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                      placeholder="www.entreprise.pf"
                      data-testid="website-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button 
                  type="submit" 
                  className="btn-coral flex-1 h-14 text-base"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Inscription en cours...
                    </>
                  ) : (
                    "Inscrire mon entreprise"
                  )}
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="h-14 px-8 border-lagoon text-lagoon hover:bg-lagoon-50"
                  data-testid="cancel-btn"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
